import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Calendar, Receipt, Package, Settings as SettingsIcon, ChevronDown,
  Ban, Trash2, Play, UserCog, Sparkles, TrendingUp, TrendingDown, Coins, ShieldCheck,
  BookOpen, LogIn, Loader2, Building2, Phone, Bot,
} from "lucide-react";

const TIER_LABEL = { basic: "Basic", pro: "Pro", premium: "Premium" };
const TIER_COLOR = { basic: "#64748B", pro: "#0EA5E9", premium: "#F59E0B" };

export default function AgencyTenantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/admin/tenants/${id}`),
        api.get("/formazione/admin/courses"),
      ]);
      setData(d.data);
      setCourses(c.data);
    } catch (e) {
      toast.error("Errore caricamento tenant");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const tenant = data?.tenant;
  const stats = data?.stats;

  const changeTier = async (tier) => {
    await api.patch(`/admin/tenants/${id}`, { plan_tier: tier });
    toast.success(`Piano aggiornato a ${TIER_LABEL[tier]}`);
    load();
  };

  const toggleSuspend = async () => {
    const suspended = !tenant.suspended;
    if (suspended && !window.confirm("Sospendere l'accesso a tutti gli utenti di questo salone?")) return;
    await api.patch(`/admin/tenants/${id}`, { suspended });
    toast.success(suspended ? "Salone sospeso" : "Salone riattivato");
    load();
  };

  const deleteTenant = async () => {
    const name = tenant.name;
    const c = window.prompt(`Per eliminare "${name}" digita: ELIMINA`);
    if (c !== "ELIMINA") return;
    if (!window.confirm(`Eliminazione DEFINITIVA di ${name}. Confermi?`)) return;
    await api.delete(`/admin/tenants/${id}?confirm=DELETE`);
    toast.success("Salone eliminato");
    nav("/admin");
  };

  const impersonate = async () => {
    try {
      const r = await api.post(`/admin/tenants/${id}/impersonate`);
      // Salva un backup token per tornare dopo, poi swappa
      const currentToken = localStorage.getItem("sp_token");
      localStorage.setItem("sp_token_admin_backup", currentToken || "");
      localStorage.setItem("sp_token", r.data.access_token);
      localStorage.setItem("sp_impersonating", "1");
      toast.success(`Accedo come ${r.data.user.email}...`);
      window.location.href = "/app";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile impersonare");
    }
  };

  const toggleCourseUnlock = async (cid) => {
    const unlocked = tenant.unlocked_course_ids || [];
    const next = unlocked.includes(cid) ? unlocked.filter((x) => x !== cid) : [...unlocked, cid];
    await api.patch(`/admin/tenants/${id}`, { unlocked_course_ids: next });
    toast.success("Sblocchi aggiornati");
    load();
  };

  if (loading || !data) {
    return <div className="p-10 text-center text-[color:var(--sp-text-secondary)]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div data-testid="agency-tenant-detail" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <button onClick={() => nav("/admin")} className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--sp-subtle)]" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
              <Building2 className="h-8 w-8 text-[color:var(--sp-brand)]" />
              {tenant.name}
              {tenant.suspended && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white uppercase tracking-widest">Sospeso</span>}
            </h1>
            <p className="text-sm text-[color:var(--sp-text-muted)] mt-1">{tenant.address || "Nessun indirizzo"} · P.IVA: {tenant.vat_number || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TierPicker current={tenant.plan_tier || "basic"} onChange={changeTier} />
          <button onClick={impersonate} data-testid="impersonate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90">
            <LogIn className="h-4 w-4" /> Impersona titolare
          </button>
          <button onClick={toggleSuspend} data-testid="suspend-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${tenant.suspended ? "border-emerald-500 text-emerald-600 hover:bg-emerald-50" : "border-amber-500 text-amber-600 hover:bg-amber-50"}`}>
            {tenant.suspended ? <><Play className="h-4 w-4" /> Riattiva</> : <><Ban className="h-4 w-4" /> Sospendi</>}
          </button>
          <button onClick={deleteTenant} data-testid="delete-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border border-red-500 text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Elimina
          </button>
        </div>
      </header>

      {/* KPI */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={Users} label="Clienti" value={stats.clients_count} sub={`${stats.active_clients_30d} attivi 30gg`} />
        <Kpi icon={UserCog} label="Staff" value={stats.staff_count} />
        <Kpi icon={TrendingUp} label="Incassi mese" value={`€ ${stats.revenue_month.toLocaleString("it-IT")}`} sub={`${stats.invoices_month} fatture`} />
        <Kpi icon={Coins} label="Incassi anno" value={`€ ${stats.revenue_year.toLocaleString("it-IT")}`} />
        <Kpi icon={TrendingDown} label="Costi mese" value={`€ ${stats.costs_month.toLocaleString("it-IT")}`} color="var(--sp-danger)" />
      </section>

      {/* Tabs */}
      <div className="border-b border-[color:var(--sp-border)] flex flex-wrap gap-1">
        {[
          { k: "overview", l: "Panoramica", i: Sparkles },
          { k: "users", l: `Utenti (${data.users.length})`, i: ShieldCheck },
          { k: "staff", l: `Staff (${data.staff.length})`, i: UserCog },
          { k: "activity", l: "Attività recente", i: Calendar },
          { k: "courses", l: "Sblocchi corsi", i: BookOpen },
          { k: "voice", l: "Telefonia", i: Phone },
          { k: "aibot", l: "AI Bot", i: Bot },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm font-bold border-b-2 transition inline-flex items-center gap-2 ${tab === t.k ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-secondary)]"}`}>
            <t.i className="h-4 w-4" />
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab tenant={tenant} />}
      {tab === "users" && <UsersTab users={data.users} tenantId={tenant.id} onChanged={load} />}
      {tab === "staff" && <StaffTab staff={data.staff} />}
      {tab === "activity" && <ActivityTab appts={data.recent_appointments} invoices={data.recent_invoices} />}
      {tab === "courses" && <CoursesTab courses={courses} tenant={tenant} onToggle={toggleCourseUnlock} />}
      {tab === "voice" && <VoiceTab tenantId={tenant.id} />}
      {tab === "aibot" && <AiBotTab tenantId={tenant.id} />}
    </div>
  );
}

// Fase 10A — attivazione capability AI Bot (ai_lead_management) per il salone.
function AiBotTab({ tenantId }) {
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/tenants/${tenantId}/capabilities`);
      setCaps(r.data.capabilities);
    } catch { setCaps(null); }
  };
  useEffect(() => { load(); }, [tenantId]);

  if (!caps) return <div className="sp-card p-5 text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>;

  const aiOn = !!caps.ai_lead_management;
  const recepOn = !!caps.receptionist_service;

  const setCap = async (key, value) => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/tenants/${tenantId}/capabilities`, { capabilities: { [key]: value } });
      setCaps(r.data.capabilities);
      toast.success("Impostazione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="sp-card p-5 max-w-2xl space-y-4" data-testid="aibot-tab">
      <div className="flex items-center gap-2">
        <Bot className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h3 className="font-display text-lg font-black">AI Bot</h3>
      </div>
      <p className="text-xs text-[color:var(--sp-text-muted)]">
        Abilita il modulo AI Bot per questo salone. Una volta attivo, la configurazione e la knowledge
        si gestiscono in <b>Receptionist › AI Bot</b> selezionando il salone.
      </p>

      <div className="flex items-center justify-between rounded-xl border border-[color:var(--sp-border)] p-4">
        <div>
          <div className="font-bold text-sm">Gestione lead con AI</div>
          <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">Capability <code>ai_lead_management</code></div>
        </div>
        <button type="button" role="switch" aria-checked={aiOn} disabled={busy}
          data-testid="aibot-capability-toggle"
          onClick={() => setCap("ai_lead_management", !aiOn)}
          className={`relative h-6 w-11 rounded-full transition-colors flex-shrink-0 ${aiOn ? "bg-[color:var(--sp-brand)]" : "bg-slate-300"} ${busy ? "opacity-50 cursor-not-allowed" : ""}`}>
          <span className={`absolute top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-200 ${aiOn ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      {aiOn && !recepOn && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="aibot-need-receptionist">
          Per far comparire il salone nel selettore dell'area Receptionist serve anche la capability
          <b> receptionist_service</b>. Vuoi attivarla?
          <button type="button" onClick={() => setCap("receptionist_service", true)} disabled={busy}
            data-testid="aibot-enable-receptionist"
            className="ml-2 underline font-bold">Attiva ora</button>
        </div>
      )}

      <div className="rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40 p-3 text-xs text-[color:var(--sp-text-muted)]">
        Nota: in questa fase l'AI Bot è configurabile e testabile nel simulatore, ma <b>non risponde ancora
        automaticamente</b> ai messaggi WhatsApp reali dei clienti.
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color = "var(--sp-brand)" }) {
  return (
    <div className="sp-card p-4 sp-anim">
      <div className="flex items-center justify-between"><span className="label-eyebrow">{label}</span><Icon className="h-4 w-4" style={{ color }} /></div>
      <div className="font-display text-2xl font-black mt-2">{value}</div>
      {sub && <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function TierPicker({ current, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="tier-picker">
      {["basic", "pro", "premium"].map((t) => (
        <button key={t} onClick={() => onChange(t)} data-testid={`tier-${t}`}
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${current === t ? "text-white" : "text-[color:var(--sp-text-secondary)]"}`}
          style={{ background: current === t ? TIER_COLOR[t] : "transparent" }}>
          {TIER_LABEL[t]}
        </button>
      ))}
    </div>
  );
}

function OverviewTab({ tenant }) {
  return (
    <div className="sp-card p-5 max-w-2xl">
      <h3 className="font-bold mb-3 text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)]">Dati fiscali</h3>
      <dl className="space-y-2 text-sm">
        <Row k="Ragione sociale" v={tenant.name} />
        <Row k="Email" v={tenant.email} />
        <Row k="Telefono" v={tenant.phone} />
        <Row k="Indirizzo" v={tenant.address} />
        <Row k="P.IVA" v={tenant.vat_number} />
        <Row k="Cod. Fiscale" v={tenant.tax_code} />
        <Row k="Regime fiscale" v={tenant.tax_regime} />
        <Row k="Forma giuridica" v={tenant.legal_form} />
      </dl>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[color:var(--sp-text-muted)] text-xs uppercase tracking-widest flex-shrink-0">{k}</dt>
      <dd className="font-medium text-right truncate">{v || "—"}</dd>
    </div>
  );
}

function UsersTab({ users, tenantId, onChanged }) {
  const [editing, setEditing] = useState(null); // {u, mode: 'reset'|'role'}
  return (
    <div className="sp-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--sp-subtle)]">
          <tr className="label-eyebrow">
            <th className="p-3 text-left">Email</th><th className="p-3 text-left">Nome</th><th className="p-3 text-left">Ruolo</th><th className="p-3 text-left">Ultimo login</th><th className="p-3 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[color:var(--sp-text-muted)]">Nessun utente</td></tr>}
          {users.map((u) => (
            <tr key={u.id} className="border-t border-[color:var(--sp-border)]" data-testid={`user-row-${u.id}`}>
              <td className="p-3 font-medium">{u.email}</td>
              <td className="p-3">{u.name || "—"}</td>
              <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[color:var(--sp-subtle)]">{u.role}</span></td>
              <td className="p-3 text-xs text-[color:var(--sp-text-muted)]">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("it-IT") : "Mai"}</td>
              <td className="p-3 text-right">
                <button onClick={() => setEditing({ u, mode: "role" })} data-testid={`user-edit-${u.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]">
                  <UserCog className="h-3.5 w-3.5" /> Modifica
                </button>
                <button onClick={() => setEditing({ u, mode: "reset" })} data-testid={`user-reset-${u.id}`}
                  className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]">
                  Reset password
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && <UserEditModal user={editing.u} mode={editing.mode} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged?.(); }} />}
    </div>
  );
}

function UserEditModal({ user, mode, onClose, onSaved }) {
  const [role, setRole] = useState(user.role);
  const [name, setName] = useState(user.name || "");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "reset" ? { new_password: newPassword } : { role, name };
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("Salvato");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="user-edit-modal"
        className="sp-card p-6 w-full max-w-md space-y-4">
        <h3 className="font-display text-xl font-black">{mode === "reset" ? "Reset password" : "Modifica utente"}</h3>
        <div className="text-sm text-[color:var(--sp-text-muted)]">{user.email}</div>
        {mode === "reset" ? (
          <label className="block">
            <span className="text-xs font-bold block mb-1">Nuova password (min 8 caratteri + 1 cifra)</span>
            <input type="password" className="sp-input" required minLength={8}
              value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password" />
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">Tutte le sessioni attive verranno invalidate.</p>
          </label>
        ) : (
          <>
            <label className="block">
              <span className="text-xs font-bold block mb-1">Nome</span>
              <input className="sp-input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-xs font-bold block mb-1">Ruolo</span>
              <select className="sp-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="role-select">
                <option value="salon_owner">Titolare salone</option>
                <option value="staff">Staff</option>
                <option value="agency_admin">Agenzia (super-admin)</option>
              </select>
            </label>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="user-edit-save"
            className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
            {busy ? "Salvo…" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}

function StaffTab({ staff }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {staff.length === 0 && <div className="sp-card p-6 text-center col-span-full text-[color:var(--sp-text-muted)]">Nessuno staff registrato</div>}
      {staff.map((s) => (
        <div key={s.id} className="sp-card p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white" style={{ background: s.color || "#06B6D4" }}>
            {(s.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{s.name}</div>
            <div className="text-xs text-[color:var(--sp-text-muted)] truncate">{s.role_title || "—"} · {s.email || "—"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityTab({ appts, invoices }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="sp-card p-5">
        <h3 className="font-bold mb-3 text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)]">Ultimi appuntamenti</h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {appts.length === 0 && <div className="text-sm text-[color:var(--sp-text-muted)]">Nessun appuntamento</div>}
          {appts.map((a) => (
            <div key={a.id} className="text-sm border-b border-[color:var(--sp-border)]/40 pb-2 last:border-0">
              <div className="font-medium">{new Date(a.start).toLocaleString("it-IT")}</div>
              <div className="text-xs text-[color:var(--sp-text-muted)]">Status: {a.status} · Notes: {a.notes || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="sp-card p-5">
        <h3 className="font-bold mb-3 text-sm uppercase tracking-widest text-[color:var(--sp-text-muted)]">Ultime fatture</h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {invoices.length === 0 && <div className="text-sm text-[color:var(--sp-text-muted)]">Nessuna fattura</div>}
          {invoices.map((i) => (
            <div key={i.id} className="flex items-center justify-between text-sm border-b border-[color:var(--sp-border)]/40 pb-2 last:border-0">
              <div>
                <div className="font-medium">{new Date(i.paid_at || i.created_at).toLocaleDateString("it-IT")}</div>
                <div className="text-xs text-[color:var(--sp-text-muted)]">{i.payment_method || "cash"}</div>
              </div>
              <div className="font-display font-black text-[color:var(--sp-brand)]">€ {(i.total || 0).toLocaleString("it-IT")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoursesTab({ courses, tenant, onToggle }) {
  const unlocked = new Set(tenant.unlocked_course_ids || []);
  return (
    <div className="sp-card overflow-hidden">
      <div className="p-4 bg-[color:var(--sp-subtle)] text-sm text-[color:var(--sp-text-secondary)]">
        I corsi con tier ≤ del piano attuale (<strong>{TIER_LABEL[tenant.plan_tier || "basic"]}</strong>) sono automaticamente disponibili. Qui puoi sbloccare manualmente corsi di tier superiore per questo salone.
      </div>
      <div className="divide-y divide-[color:var(--sp-border)]">
        {courses.map((c) => {
          const isManual = unlocked.has(c.id);
          return (
            <div key={c.id} className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded bg-gradient-to-br from-cyan-400 to-violet-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{c.title}</div>
                <div className="text-xs text-[color:var(--sp-text-muted)]">Tier: <span className="font-bold" style={{ color: TIER_COLOR[c.required_tier] }}>{TIER_LABEL[c.required_tier]}</span> · {c.lessons_count} lezioni</div>
              </div>
              <button onClick={() => onToggle(c.id)} data-testid={`course-toggle-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${isManual ? "bg-[color:var(--sp-brand)] text-white border-[color:var(--sp-brand)]" : "border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)]"}`}>
                {isManual ? "Sbloccato manualmente" : "Sblocca"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}



// Fase 9A — Telefonia (Verified Outgoing Caller ID). Solo agency_admin.
function VoiceTab({ tenantId }) {
  const [cfg, setCfg] = useState(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/agency/voice/tenants/${tenantId}/config`);
      setCfg(r.data);
      setPhone(r.data.config?.caller_id_phone_number || "");
    } catch { setCfg(null); }
  };
  useEffect(() => { load(); }, [tenantId]);

  if (!cfg) return <div className="sp-card p-5 text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>;
  if (!cfg.outbound_voice) {
    return (
      <div className="sp-card p-5 text-sm text-[color:var(--sp-text-muted)]" data-testid="voice-disabled">
        Chiamate da Memento non abilitate per questo salone. Attiva la capability <b>outbound_voice</b> per configurare la telefonia.
      </div>
    );
  }

  const st = cfg.config?.caller_id_status || "not_configured";
  const BADGE = {
    verified: ["✓ Verificato", "text-emerald-700 bg-emerald-50 border-emerald-200"],
    verification_pending: ["Verifica in corso", "text-amber-700 bg-amber-50 border-amber-200"],
    verification_failed: ["Verifica fallita", "text-rose-700 bg-rose-50 border-rose-200"],
    not_configured: ["Non configurato", "text-slate-500 bg-slate-50 border-slate-200"],
  }[st] || ["—", ""];

  const errMsg = (e, fb) => {
    const d = e?.response?.data?.detail;
    if (!d) return fb;
    if (typeof d === "string") return d;
    return d.message || fb;
  };
  const saveNumber = async () => {
    setBusy(true);
    try { await api.put(`/agency/voice/tenants/${tenantId}/config`, { caller_id_phone_number: phone }); setCode(null); toast.success("Numero salvato"); await load(); }
    catch (e) { toast.error(errMsg(e, "Errore salvataggio")); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const r = await api.post(`/agency/voice/tenants/${tenantId}/verify`); setCode(r.data.validation_code); toast.success("Twilio sta chiamando il numero"); await load(); }
    catch (e) { toast.error(errMsg(e, "Verifica non avviata")); }
    finally { setBusy(false); }
  };
  const reconcile = async () => {
    setBusy(true);
    try { await api.post(`/agency/voice/tenants/${tenantId}/reconcile`); toast.success("Stato aggiornato"); await load(); }
    catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Rimuovere la configurazione telefonica di questo salone?")) return;
    setBusy(true);
    try { await api.delete(`/agency/voice/tenants/${tenantId}/config`); setCode(null); toast.success("Configurazione rimossa"); await load(); }
    catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="sp-card p-5 max-w-2xl space-y-4" data-testid="voice-tab">
      <div className="flex items-center gap-2">
        <Phone className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h3 className="font-display text-lg font-black">Telefonia · Caller ID</h3>
      </div>
      <p className="text-xs text-[color:var(--sp-text-muted)]">Identità telefonica del salone usata dall'Agency: quando il canale di acquisizione è impostato su <b>Salone</b>, le chiamate della receptionist mostrano questo Caller ID verificato. Il numero resta col tuo operatore telefonico e il salone non telefona da Memento.</p>
      {!cfg.provider_configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="voice-provider-missing">
          Provider Twilio non ancora configurato lato server: la verifica sarà disponibile dopo l'impostazione delle credenziali.
        </div>
      )}
      <div>
        <label className="text-xs font-bold">Numero Caller ID del salone (formato E.164)</label>
        <div className="flex gap-2 mt-1">
          <input data-testid="voice-phone-input" className="sp-input" placeholder="+393331234567" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <button data-testid="voice-save-btn" onClick={saveNumber} disabled={busy} className="sp-btn-ghost text-sm whitespace-nowrap disabled:opacity-50">Salva numero</button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">Stato</span>
        <span data-testid="voice-status" className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${BADGE[1]}`}>{BADGE[0]}</span>
      </div>
      {code && (
        <div className="rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)] p-3 text-sm" data-testid="voice-validation-code">
          Twilio sta chiamando <b>{cfg.config?.caller_id_phone_number}</b>. Quando rispondi, inserisci questo codice:
          <div className="text-2xl font-black tracking-widest mt-1">{code}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button data-testid="voice-verify-btn" onClick={verify} disabled={busy || !cfg.caller_id || st === "verified"} className="sp-btn-primary text-sm disabled:opacity-50">{st === "verification_failed" ? "Riprova verifica" : "Verifica numero"}</button>
        <button data-testid="voice-reconcile-btn" onClick={reconcile} disabled={busy} className="sp-btn-ghost text-sm disabled:opacity-50">Aggiorna stato</button>
        <button data-testid="voice-remove-btn" onClick={remove} disabled={busy} className="text-sm text-[color:var(--sp-danger)] px-3 disabled:opacity-50">Rimuovi configurazione</button>
      </div>
    </div>
  );
}